import { TIERS_LISTED, type Tier } from "../../src/tiers.ts";
import type { Item, Person } from "../../src/web/types.ts";
import { faceNames, inOrder } from "./faces.ts";

// Where people's wants meet, worked out from everyone's interest, never stored: People's Together, and who
// else wants each suggestion on Ideas. Only places and goals are rated; stays, moves and the rest are the
// plan.

// Lists read Yes first: Yes, Maybe, No (the founder's pick, 2026-10-22).
export const TIERS = TIERS_LISTED;
export type { Tier };

const wantable = (i: Item) => (i.type === "place" || i.type === "goal") && i.status !== "dropped";

export const tierOf = (i: Pick<Item, "interest">, person: string): Tier | null => {
  const t = i.interest.find((x) => x.person === person)?.tier;
  return t === "yes" || t === "maybe" || t === "no" ? t : null;
};

// Together (features/people.md, "Together"): for the people picked, what they all say Yes, all Maybe and
// all No, and where they differ, each rated by every one of them; a place not every one of them
// has rated is in none, as it waits on someone's rating. One person picked is their own list. Where they
// differ, what more of them want comes first: the most yeses, then the most who said Yes or Maybe.
export interface Together {
  yes: Item[];
  maybe: Item[];
  no: Item[];
  differ: { item: Item; tiers: { person: Person; tier: Tier }[] }[];
}

export function together(items: Item[], picked: Person[]): Together {
  const out: Together = { yes: [], maybe: [], no: [], differ: [] };
  if (picked.length === 0) return out;
  for (const item of items.filter(wantable)) {
    const tiers = picked.flatMap((person) => {
      const tier = tierOf(item, person.id);
      return tier ? [{ person, tier }] : [];
    });
    if (tiers.length < picked.length) continue;
    const first = tiers[0]?.tier;
    if (first && tiers.every((t) => t.tier === first)) out[first].push(item);
    else out.differ.push({ item, tiers });
  }
  const count = (d: Together["differ"][number], tier: Tier) => d.tiers.filter((t) => t.tier === tier).length;
  const wants = (d: Together["differ"][number]) => count(d, "yes") + count(d, "maybe");
  out.differ.sort(
    (a, b) => count(b, "yes") - count(a, "yes") || wants(b) - wants(a) || a.item.title.localeCompare(b.item.title),
  );
  return out;
}

// People in groups by the tier they gave, Yes first, each group in the row of faces' order (You first, then
// by first name); tiers nobody gave are left out.
export function byTierGiven(
  tiers: { person: Person; tier: Tier }[],
  members: Person[],
  you: string,
): { tier: Tier; people: Person[] }[] {
  const order = inOrder(members, you).map((m) => m.id);
  const at = (p: Person) => {
    const i = order.indexOf(p.id);
    return i < 0 ? order.length : i;
  };
  return TIERS.flatMap((tier) => {
    const people = tiers
      .filter((t) => t.tier === tier)
      .map((t) => t.person)
      .sort((a, b) => at(a) - at(b));
    return people.length > 0 ? [{ tier, people }] : [];
  });
}

// Who else wants it, on every suggestion (features/recommendations.md, "Who else wants it, on every row"):
// everyone still in the trip but me who said Yes or Maybe, Yes first, each by first name: "Yes from Mei ·
// Maybe from Ben and Jon"; or "Nobody else yet" (whether they haven't rated it or said No). Past
// three people, how many in each, so the line stays short on a phone: "Yes from 8 · Maybe from 6" (their
// faces, and `all`, say who). `faces`: everyone counted, in the same order.
export const NAMED = 3;

export function whoElse(
  item: Pick<Item, "interest">,
  members: Person[],
  you: string,
): { faces: Person[]; text: string; all: string } {
  const wanting = members.flatMap((person) => {
    if (person.id === you) return [];
    const tier = tierOf(item, person.id);
    return tier === "yes" || tier === "maybe" ? [{ person, tier }] : [];
  });
  const groups = byTierGiven(wanting, members, you);
  if (groups.length === 0) return { faces: [], text: "Nobody else yet", all: "Nobody else yet" };
  const names = faceNames(members, you);
  const label = (tier: Tier) => (tier === "yes" ? "Yes" : "Maybe");
  const all = groups
    .map(({ tier, people }) => `${label(tier)} from ${listed(people.map((p) => names.get(p.id) ?? p.name))}`)
    .join(" · ");
  const text =
    wanting.length <= NAMED
      ? all
      : groups.map(({ tier, people }) => `${label(tier)} from ${people.length}`).join(" · ");
  return { faces: groups.flatMap((g) => g.people), text, all };
}

// Everyone else's rating of it by tier, Yes first (Yes, Maybe, No), for the deck's card in a big group
// (DESIGN.md, "Rating one at a time"): each tier's people in the row of faces' order, and who they are in
// words as the who-else line names them ("Ana H., Ben and Jon"), for hover and screen readers.
export function othersByTier(
  item: Pick<Item, "interest">,
  members: Person[],
  you: string,
): { tier: Tier; people: Person[]; names: string }[] {
  const rated = members.flatMap((person) => {
    if (person.id === you) return [];
    const tier = tierOf(item, person.id);
    return tier ? [{ person, tier }] : [];
  });
  const names = faceNames(members, you);
  return byTierGiven(rated, members, you).map(({ tier, people }) => ({
    tier,
    people,
    names: listed(people.map((p) => names.get(p.id) ?? p.name)),
  }));
}

// "Mei", "Mei and Ben", "Mei, Ben and Jon".
function listed(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
